import {subscribePush as apiSubscribe, unsubscribePush as apiUnsubscribe} from '../api/notifications.js'

const SUBSCRIPTION_ID_KEY = 'verdasync.pushSubscriptionId'

/**
 * @typedef {'unsupported'|'default'|'granted'|'denied'} PermissionState
 */

/**
 * @typedef {Object} PermissionInfo
 * @property {boolean} supported    true when Notification + PushManager APIs exist
 * @property {PermissionState} state
 * @property {boolean} subscribed   whether we have an active PushSubscription
 */

/**
 * @return {boolean}
 */
export function isPushSupported() {
    return typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window
}

/**
 * Read the current permission + subscription status.
 * Safe to call from any context.
 * @return {Promise<PermissionInfo>}
 */
export async function getPermissionInfo() {
    if (!isPushSupported())
        return {supported: false, state: 'unsupported', subscribed: false}
    const state = Notification.permission
    let subscribed = false
    try {
        const reg = await navigator.serviceWorker.getRegistration()
        if (reg) {
            const sub = await reg.pushManager.getSubscription()
            subscribed = !!sub
        }
    } catch {
        subscribed = false
    }
    return {supported: true, state, subscribed}
}

/**
 * Convert a URL-safe base64 VAPID public key to the Uint8Array form
 * accepted by `pushManager.subscribe`.
 * @param {string} base64
 * @return {Uint8Array}
 */
export function urlBase64ToUint8Array(base64) {
    const padding = '='.repeat((4 - base64.length % 4) % 4)
    const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
    const raw = atob(normalized)
    const output = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i++)
        output[i] = raw.charCodeAt(i)
    return output
}

/**
 * Convert an ArrayBuffer (subscription `keys.p256dh` / `keys.auth`) to a
 * URL-safe base64 string suitable for transport to the backend.
 * @param {ArrayBuffer|null} buffer
 * @return {string}
 */
function bufferToBase64(buffer) {
    if (!buffer)
        return ''
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.length; i++)
        binary += String.fromCharCode(bytes[i])
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Request notification permission, subscribe the SW push manager, and
 * register the subscription server-side.
 *
 * Throws if push is unsupported, the user denies permission, or the
 * VAPID public key is not configured.
 *
 * @return {Promise<{subscriptionId: string}>}
 */
export async function enablePush() {
    if (!isPushSupported())
        throw new Error('Push notifications are not supported on this device.')

    const vapidPublicKey = process.env.VAPID_PUBLIC_KEY
    if (!vapidPublicKey)
        throw new Error('Push notifications are not configured (missing VAPID public key).')

    const permission = await Notification.requestPermission()
    if (permission !== 'granted')
        throw new Error('Notification permission was not granted.')

    const reg = await navigator.serviceWorker.ready
    let subscription = await reg.pushManager.getSubscription()
    if (!subscription) {
        subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        })
    }

    const keys = subscription.toJSON().keys || {}
    const body = {
        endpoint: subscription.endpoint,
        keys: {
            p256dh: keys.p256dh || bufferToBase64(subscription.getKey('p256dh')),
            auth: keys.auth || bufferToBase64(subscription.getKey('auth'))
        },
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
    }
    const response = await apiSubscribe(body)
    if (response?.subscriptionId)
        localStorage.setItem(SUBSCRIPTION_ID_KEY, response.subscriptionId)
    return response
}

/**
 * Unsubscribe locally and notify the backend.
 * No-op if there is no active subscription.
 * @return {Promise<void>}
 */
export async function disablePush() {
    if (!isPushSupported())
        return

    const subscriptionId = localStorage.getItem(SUBSCRIPTION_ID_KEY)
    try {
        const reg = await navigator.serviceWorker.getRegistration()
        const sub = reg ? await reg.pushManager.getSubscription() : null
        if (sub)
            await sub.unsubscribe()
    } catch {
        // ignore — we still attempt the server-side unsubscribe below
    }

    if (subscriptionId) {
        try {
            await apiUnsubscribe(subscriptionId)
        } catch {
            // backend will GC orphaned rows eventually
        }
        localStorage.removeItem(SUBSCRIPTION_ID_KEY)
    }
}
